import React from 'react';
import { createPortal } from 'react-dom';
import styles from './OptionDropdown.module.css';

let optionDropdownSeq = 0;

/**
 * Compact option menu: a text trigger and a white stacked list.
 * The panel is portaled so it is not clipped by overflow on the trigger's parents.
 *
 * Props:
 * - options: { value: string, label: string }[]
 * - value: string
 * - onChange: (value: string) => void
 * - ariaLabel?: string
 * - id?: string
 * - open?: boolean          controlled when a boolean is passed
 * - onOpenChange?: (open: boolean) => void
 * - disabled?: boolean
 * - className?: string
 */
class OptionDropdown extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      highlightedIndex: 0,
      menuTop: 0,
      menuLeft: 0,
    };
    this.rootRef = React.createRef();
    this.triggerRef = React.createRef();
    this.menuRef = React.createRef();
    this.listId = props.id || `option-dropdown-${optionDropdownSeq++}`;
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handlePointerDown);
    window.addEventListener('resize', this.updateMenuPosition);
    window.addEventListener('scroll', this.updateMenuPosition, true);
    if (this.isOpen()) {
      const pos = this.measureMenu();
      this.setState({
        menuTop: pos.top,
        menuLeft: pos.left,
        highlightedIndex: this.initialHighlight(),
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const open = this.isOpen();
    const wasOpen = this.wasOpen(prevProps, prevState);
    if (open && !wasOpen) {
      const pos = this.measureMenu();
      const highlightedIndex = this.initialHighlight();
      if (pos.top !== this.state.menuTop || pos.left !== this.state.menuLeft || highlightedIndex !== this.state.highlightedIndex) {
        this.setState({ menuTop: pos.top, menuLeft: pos.left, highlightedIndex });
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handlePointerDown);
    window.removeEventListener('resize', this.updateMenuPosition);
    window.removeEventListener('scroll', this.updateMenuPosition, true);
  }

  options = () => (Array.isArray(this.props.options) ? this.props.options : []);

  isOpen = () => (typeof this.props.open === 'boolean' ? this.props.open : this.state.open);

  wasOpen = (prevProps, prevState) => (
    typeof prevProps.open === 'boolean' ? prevProps.open : prevState.open
  );

  initialHighlight = () => {
    const options = this.options();
    const index = options.findIndex((option) => option.value === this.props.value);
    return index >= 0 ? index : 0;
  };

  measureMenu = () => {
    const trigger = this.triggerRef.current;
    if (!trigger || typeof trigger.getBoundingClientRect !== 'function') {
      return { top: 0, left: 0 };
    }
    const rect = trigger.getBoundingClientRect();
    return { top: rect.bottom + 6, left: rect.left };
  };

  setOpen = (next) => {
    const patch = {};
    if (typeof this.props.open !== 'boolean') patch.open = next;
    if (next) {
      const pos = this.measureMenu();
      patch.menuTop = pos.top;
      patch.menuLeft = pos.left;
      patch.highlightedIndex = this.initialHighlight();
    }
    if (Object.keys(patch).length) this.setState(patch);
    if (typeof this.props.onOpenChange === 'function') this.props.onOpenChange(next);
  };

  updateMenuPosition = () => {
    if (!this.isOpen()) return;
    const pos = this.measureMenu();
    if (pos.top === this.state.menuTop && pos.left === this.state.menuLeft) return;
    this.setState({ menuTop: pos.top, menuLeft: pos.left });
  };

  handlePointerDown = (event) => {
    if (!this.isOpen()) return;
    const target = event.target;
    if (this.rootRef.current && this.rootRef.current.contains(target)) return;
    if (this.menuRef.current && this.menuRef.current.contains(target)) return;
    this.setOpen(false);
  };

  handleToggle = () => {
    if (this.props.disabled) return;
    this.setOpen(!this.isOpen());
  };

  handleKeyDown = (event) => {
    if (this.props.disabled) return;
    const options = this.options();
    const open = this.isOpen();

    if (event.key === 'Escape') {
      if (!open) return;
      event.preventDefault();
      this.setOpen(false);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        this.setOpen(true);
        return;
      }
      if (!options.length) return;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      this.setState((state) => ({
        highlightedIndex: (state.highlightedIndex + delta + options.length) % options.length,
      }));
      return;
    }

    if (event.key === 'Enter' && open) {
      event.preventDefault();
      const option = options[this.state.highlightedIndex];
      if (option) this.select(option.value);
    }
  };

  select = (value) => {
    if (typeof this.props.onChange === 'function') this.props.onChange(value);
    this.setOpen(false);
  };

  render() {
    const options = this.options();
    const open = this.isOpen();
    const selected = options.find((option) => option.value === this.props.value);
    const label = selected ? selected.label : (this.props.value || '');
    const highlighted = options[this.state.highlightedIndex];
    const rootClass = [styles.root, this.props.className].filter(Boolean).join(' ');

    const menu = open ? (
      <ul
        ref={this.menuRef}
        id={this.listId}
        role="listbox"
        aria-label={this.props.ariaLabel || 'Options'}
        className={styles.menu}
        style={{ top: this.state.menuTop, left: this.state.menuLeft }}
      >
        {options.map((option, index) => {
          const isSelected = option.value === this.props.value;
          const isHighlighted = index === this.state.highlightedIndex;
          const className = [
            styles.option,
            isSelected ? styles.optionSelected : '',
            isHighlighted ? styles.optionHighlighted : '',
          ].filter(Boolean).join(' ');
          return (
            <li
              key={option.value}
              id={`${this.listId}-opt-${index}`}
              role="option"
              aria-selected={isSelected}
              className={className}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => this.setState({ highlightedIndex: index })}
              onClick={() => this.select(option.value)}
            >
              {option.label}
            </li>
          );
        })}
      </ul>
    ) : null;

    return (
      <div
        ref={this.rootRef}
        className={rootClass}
        onKeyDown={this.handleKeyDown}
      >
        <button
          ref={this.triggerRef}
          type="button"
          className={styles.trigger}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? this.listId : undefined}
          aria-activedescendant={open && highlighted ? `${this.listId}-opt-${this.state.highlightedIndex}` : undefined}
          aria-label={this.props.ariaLabel || label || 'Options'}
          disabled={this.props.disabled}
          onClick={this.handleToggle}
        >
          {label}
        </button>
        {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
      </div>
    );
  }
}

export default OptionDropdown;
